'use client';

import { useState, useEffect } from 'react';
import { useCookieConsent } from '@/lib/hooks/useCookieConsent';
import styles from './CookiePreferences.module.scss';

interface CookiePreferencesProps {
  copy: {
    title: string;
    description: string;
    currentStatus: {
      label: string;
      accepted: string;
      rejected: string;
      pending: string;
    };
    categories: {
      essential: {
        title: string;
        description: string;
        alwaysOn: string;
      };
      nonEssential: {
        title: string;
        description: string;
      };
    };
    buttons: {
      acceptAll: string;
      rejectAll: string;
    };
    successMessage: string;
  };
}

export function CookiePreferences({ copy }: CookiePreferencesProps) {
  const { consentStatus, acceptCookies, rejectCookies } = useCookieConsent();
  const [nonEssentialEnabled, setNonEssentialEnabled] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Initialize toggle state based on current consent
  useEffect(() => {
    if (consentStatus === 'accepted') {
      setNonEssentialEnabled(true);
    } else if (consentStatus === 'rejected') {
      setNonEssentialEnabled(false);
    }
  }, [consentStatus]);

  const handleAcceptAll = () => {
    setNonEssentialEnabled(true);
    acceptCookies();
    showSuccessMessage();
  };

  const handleRejectAll = () => {
    setNonEssentialEnabled(false);
    rejectCookies();
    showSuccessMessage();
  };

  const showSuccessMessage = () => {
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const getStatusText = () => {
    switch (consentStatus) {
      case 'accepted':
        return copy.currentStatus.accepted;
      case 'rejected':
        return copy.currentStatus.rejected;
      default:
        return copy.currentStatus.pending;
    }
  };

  const getStatusClass = () => {
    switch (consentStatus) {
      case 'accepted':
        return styles.statusAccepted;
      case 'rejected':
        return styles.statusRejected;
      default:
        return styles.statusPending;
    }
  };

  return (
    <div className={styles.preferences}>
      <div className={styles.header}>
        <h2 className={styles.title}>{copy.title}</h2>
        <p className={styles.description}>{copy.description}</p>

        <div className={styles.statusBar}>
          <span className={styles.statusLabel}>{copy.currentStatus.label}</span>
          <span className={`${styles.statusValue} ${getStatusClass()}`}>
            {getStatusText()}
          </span>
        </div>
      </div>

      {showSuccess && (
        <div className={styles.successMessage}>{copy.successMessage}</div>
      )}

      <div className={styles.categories}>
        {/* Essential Cookies - Always On */}
        <div className={styles.category}>
          <div className={styles.categoryHeader}>
            <h3 className={styles.categoryTitle}>
              {copy.categories.essential.title}
            </h3>
            <div className={styles.toggleWrapper}>
              <span className={styles.alwaysOn}>
                {copy.categories.essential.alwaysOn}
              </span>
            </div>
          </div>
          <p className={styles.categoryDescription}>
            {copy.categories.essential.description}
          </p>
        </div>

        {/* Non-Essential Cookies (Analytics + Marketing) */}
        <div className={styles.category}>
          <div className={styles.categoryHeader}>
            <h3 className={styles.categoryTitle}>
              {copy.categories.nonEssential.title}
            </h3>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={nonEssentialEnabled}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setNonEssentialEnabled(newValue);
                  if (newValue) {
                    acceptCookies();
                  } else {
                    rejectCookies();
                  }
                  showSuccessMessage();
                }}
                className={styles.toggleInput}
              />
              <span className={styles.toggleSlider}></span>
            </label>
          </div>
          <p className={styles.categoryDescription}>
            {copy.categories.nonEssential.description}
          </p>
        </div>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonSecondary}`}
          onClick={handleRejectAll}
        >
          {copy.buttons.rejectAll}
        </button>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonPrimary}`}
          onClick={handleAcceptAll}
        >
          {copy.buttons.acceptAll}
        </button>
      </div>
    </div>
  );
}
